import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ChipGroup } from "@/components/ChipGroup";
import { api, errMsg } from "@/lib/api";
import { MEETING_TYPES, label, toLocalInput, presetAt } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";

export const ScheduleMeetingDialog = ({ lead, open, onOpenChange, onDone }) => {
  const { user } = useAuth();
  const [type, setType] = useState("DISCOVERY");
  const [channel, setChannel] = useState("GOOGLE_MEET");
  const [start, setStart] = useState(toLocalInput(presetAt("tomorrow")));
  const [dur, setDur] = useState(45);
  const [agenda, setAgenda] = useState("");
  const [demoUrl, setDemoUrl] = useState(lead?.website_demo_url || "");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) setDemoUrl(lead?.website_demo_url || ""); }, [open, lead?.id, lead?.website_demo_url]);

  const submit = async () => {
    setBusy(true);
    try {
      const s = new Date(start);
      await api.post("/meetings", { lead_id: lead.id, meeting_type: type, channel, agenda, scheduled_start: s.toISOString(), scheduled_end: new Date(s.getTime() + dur * 60000).toISOString(), ...(type === "TECH_DEMO" && user?.role === "ADMIN" ? { website_demo_url: demoUrl } : {}) });
      toast.success("Meeting scheduled · invite dispatched (mocked)");
      onOpenChange(false); onDone?.();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="schedule-meeting-dialog" className="border-neutral-200 bg-white text-[#1A1A1A] sm:max-w-md">
        <DialogHeader><DialogTitle className="font-display">Schedule meeting</DialogTitle><DialogDescription className="text-neutral-500">{lead?.full_name} · {lead?.company_name}</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <div><p className="mb-2 text-xs text-neutral-500">Focus</p><ChipGroup options={MEETING_TYPES} value={type} onChange={setType} testPrefix="meeting-type" getLabel={label} /></div>
          <div><p className="mb-2 text-xs text-neutral-500">Channel</p><ChipGroup options={["GOOGLE_MEET", "PHONE", "IN_PERSON", "TEAMS"]} value={channel} onChange={setChannel} testPrefix="meeting-channel" getLabel={label} /></div>
          <div><p className="mb-2 text-xs text-neutral-500">Start</p>
            <input type="datetime-local" data-testid="meeting-start-input" value={start} onChange={(e) => setStart(e.target.value)} className="w-full rounded-lg border border-neutral-200 bg-white px-3 py-2 text-sm" />
          </div>
          <div><p className="mb-2 text-xs text-neutral-500">Duration</p><ChipGroup options={[30, 45, 60, 90]} value={dur} onChange={setDur} testPrefix="meeting-duration" getLabel={(d) => `${d} min`} /></div>
          {type === "TECH_DEMO" && user?.role === "ADMIN" && <Input data-testid="meeting-demo-url-input" type="url" placeholder="Demo URL for agents (https://...)" value={demoUrl} onChange={(e) => setDemoUrl(e.target.value)} className="border-neutral-200 bg-white" />}
          <Input data-testid="meeting-agenda-input" placeholder="Agenda (optional)" value={agenda} onChange={(e) => setAgenda(e.target.value)} className="border-neutral-200 bg-white" />
          <Button data-testid="meeting-submit-btn" disabled={busy || !start} onClick={submit} className="h-11 w-full bg-[#1F9E7A] hover:bg-[#178566]">Schedule & send invite</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
